import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Autocomplete, {
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  createPricebookPeriod,
  readPricebookPeriodRefusal,
  updatePricebookPeriod,
} from "../../../../services";
import { PricebookPeriod } from "../../../../types/price-book.type";
import { todayInBangkok } from "../../../../utils/date";
import ConfirmModal from "../../../ConfirmModal";
import AmountInput from "../../shared/AmountInput";
import PeriodPricePreview from "./PeriodPricePreview";
import {
  PeriodForm,
  PeriodFormErrors,
  PeriodLine,
  buildPeriodCreateRequest,
  buildPeriodUpdateRequest,
  emptyPeriodForm,
  hasPeriodStarted,
  toPeriodForm,
  validatePeriodForm,
} from "./periodModel";

/** The API keeps a period's price and % at 2 dp. */
const MONEY_FRACTION_DIGITS = 2;
/** Mirrors `pricebook_item_period.note`, varchar(255). */
const NOTE_MAX_LENGTH = 255;
const PICKER_FORMAT = "DD/MM/YYYY";
const SAVE_FAILED = "บันทึกช่วงราคาไม่สำเร็จ กรุณาลองอีกครั้ง";
const START_LOCKED_HINT = "ช่วงเริ่มใช้แล้ว เปลี่ยนวันเริ่มไม่ได้";
const PRICE_HINT =
  "ช่องที่เว้นว่างใช้ค่าของรายการ ต้องกรอกอย่างน้อยหนึ่งช่อง หมดช่วงแล้วกลับไปขายที่ราคาหลัก";

/** A new period (of a preset product, or one picked in the dialog), or a stored one to edit. */
export type PeriodDialogTarget =
  | { kind: "create"; productCode: string | null }
  | { kind: "edit"; period: PricebookPeriod };

const toInitialForm = (target: PeriodDialogTarget): PeriodForm =>
  target.kind === "edit"
    ? toPeriodForm(target.period)
    : emptyPeriodForm(target.productCode ?? "");

const formatLineOption = (line: PeriodLine): string =>
  `${line.productCode} ${line.productName}`;

interface PeriodDialogProps {
  /** Null while the dialog is closed. */
  target: PeriodDialogTarget | null;
  pricebookUuid: string;
  /** The book's stored %, which a line without its own follows. */
  bookDiscountPercent: number;
  /** The stored lines a period can go on (each has a unit price of its own). */
  lines: PeriodLine[];
  onClose: () => void;
  /** A period was written: the caller closes the dialog and reads the book again. */
  onSaved: () => void;
}

/**
 * Adds or edits one price period: its days (Bangkok, both inclusive), its own unit price and/or
 * %, and what the line would sell at inside it — computed as the API computes it.
 */
export default function PeriodDialog({
  target,
  pricebookUuid,
  bookDiscountPercent,
  lines,
  onClose,
  onSaved,
}: PeriodDialogProps) {
  const [form, setForm] = useState<PeriodForm>(() => emptyPeriodForm(""));
  const [errors, setErrors] = useState<PeriodFormErrors>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Each opening starts from what it was opened on; nothing typed for another period carries over.
  useEffect(() => {
    if (!target) return;
    setForm(toInitialForm(target));
    setErrors({});
    setErrorMessage("");
  }, [target]);

  const storedPeriod = target?.kind === "edit" ? target.period : null;
  const isPickingProduct = target?.kind === "create" && !target.productCode;
  const line = lines.find((option) => option.productCode === form.productCode);
  const productLabel = `${form.productCode} ${
    storedPeriod?.product_name ?? line?.productName ?? ""
  }`;
  const isStartLocked = storedPeriod ? hasPeriodStarted(storedPeriod.status) : false;
  const today = todayInBangkok();
  const title = storedPeriod ? "แก้ไขช่วงราคา" : "เพิ่มช่วงราคา";
  // A period that has started keeps its first day, which may already be in the past.
  const startMinDate = isStartLocked ? undefined : today;
  const startHelperText =
    errors.startDate ?? (isStartLocked ? START_LOCKED_HINT : undefined);
  // The last day cannot come before the first, so the picker opens there once a start is picked.
  const endMinDate =
    form.startDate?.isValid() && form.startDate.isAfter(today, "day")
      ? form.startDate
      : today;
  // An empty field keeps the line's value, so the field shows that value as its placeholder.
  const unitPricePlaceholder = line ? String(line.unitPrice) : "";
  const discountPlaceholder = String(
    line?.ownDiscountPercent ?? bookDiscountPercent,
  );

  const changeForm = (changes: Partial<PeriodForm>) =>
    setForm((prev) => ({ ...prev, ...changes }));

  const handleChangeProduct = (
    _event: SyntheticEvent,
    picked: PeriodLine | null,
  ) => changeForm({ productCode: picked?.productCode ?? "" });
  const handleChangeStartDate = (startDate: Dayjs | null) =>
    changeForm({ startDate });
  const handleChangeEndDate = (endDate: Dayjs | null) => changeForm({ endDate });
  const handleChangeUnitPrice = (unitPrice: string) => changeForm({ unitPrice });
  const handleChangeDiscount = (discountPercent: string) =>
    changeForm({ discountPercent });
  const handleChangeNote = (event: ChangeEvent<HTMLInputElement>) =>
    changeForm({ note: event.target.value });

  const renderProductInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      size="small"
      label="สินค้า"
      error={Boolean(errors.productCode)}
      helperText={errors.productCode}
    />
  );

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  /** Writes the form; false when an edit changed nothing, so there is nothing to read again. */
  const writePeriod = async (): Promise<boolean> => {
    if (!storedPeriod) {
      const body = buildPeriodCreateRequest(form);
      if (!body) return false;
      await createPricebookPeriod(pricebookUuid, body);
      return true;
    }
    const body = buildPeriodUpdateRequest(form, storedPeriod);
    if (!body || Object.keys(body).length === 0) return false;
    await updatePricebookPeriod(pricebookUuid, storedPeriod.uuid, body);
    return true;
  };

  const handleSave = async () => {
    const found = validatePeriodForm(form, today, storedPeriod);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    setErrorMessage("");
    try {
      const isWritten = await writePeriod();
      if (isWritten) onSaved();
      else onClose();
    } catch (error) {
      setErrorMessage(readPricebookPeriodRefusal(error) ?? SAVE_FAILED);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmModal
      open={Boolean(target)}
      title={title}
      showCloseButton
      confirmLabel="บันทึกช่วงราคา"
      loading={saving}
      onClose={handleClose}
      onConfirm={handleSave}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
        <Stack gap={2} sx={{ pt: 1 }}>
          {isPickingProduct ? (
            <Autocomplete
              options={lines}
              value={line ?? null}
              getOptionLabel={formatLineOption}
              onChange={handleChangeProduct}
              renderInput={renderProductInput}
            />
          ) : (
            <Typography fontWeight={600}>{productLabel}</Typography>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <DatePicker
              label="วันเริ่ม"
              value={form.startDate}
              format={PICKER_FORMAT}
              minDate={startMinDate}
              disabled={isStartLocked}
              onChange={handleChangeStartDate}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: Boolean(errors.startDate),
                  helperText: startHelperText,
                },
              }}
            />
            <DatePicker
              label="วันสิ้นสุด"
              value={form.endDate}
              format={PICKER_FORMAT}
              minDate={endMinDate}
              onChange={handleChangeEndDate}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: Boolean(errors.endDate),
                  helperText: errors.endDate,
                },
              }}
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <AmountInput
              label="ราคาต่อหน่วยในช่วง (รวม VAT)"
              value={form.unitPrice}
              maxFractionDigits={MONEY_FRACTION_DIGITS}
              placeholder={unitPricePlaceholder}
              error={errors.unitPrice}
              onChange={handleChangeUnitPrice}
            />
            <AmountInput
              label="ส่วนลด % ในช่วง"
              value={form.discountPercent}
              maxFractionDigits={MONEY_FRACTION_DIGITS}
              placeholder={discountPlaceholder}
              error={errors.discountPercent}
              onChange={handleChangeDiscount}
            />
          </Stack>
          <Typography
            variant="caption"
            color={errors.price ? "error" : "text.secondary"}
          >
            {errors.price ?? PRICE_HINT}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="หมายเหตุ"
            value={form.note}
            onChange={handleChangeNote}
            slotProps={{ htmlInput: { maxLength: NOTE_MAX_LENGTH } }}
          />
          <PeriodPricePreview
            form={form}
            line={line}
            bookDiscountPercent={bookDiscountPercent}
          />
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </LocalizationProvider>
    </ConfirmModal>
  );
}
